import type { FindingCategory, FindingStatus, Severity } from '@/graphql/generated/graphql'

/**
 * The eight visual states a report can express, and the rules for deriving one
 * from a finding.
 *
 * A tone is just a string. Components put it on a `data-tone` attribute and the
 * token block in `main.css` supplies the colours. Nothing in TypeScript knows
 * what "critical" looks like, which is what stops the summary counts and the
 * finding rows drifting apart: there is one definition of each tone and both
 * read it.
 */
export type Tone =
  | 'pass'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'
  | 'unknown'
  | 'untested'
  | 'neutral'

/** The glyph a tone carries. Colour is never the only signal. */
export type ToneGlyph = 'check' | 'alert' | 'question' | 'skip' | 'dot'

export interface ToneStyle {
  readonly tone: Tone
  readonly glyph: ToneGlyph
  /** Written out in full next to the glyph. Third signal after colour and shape. */
  readonly label: string
}

const SEVERITY_TONE: Readonly<Record<Severity, Tone>> = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info',
}

export const SEVERITY_LABEL: Readonly<Record<Severity, string>> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  INFO: 'Info',
}

/**
 * Worst first, matching the order the server returns `failuresBySeverity` in.
 *
 * Only used for rendering a legend and for picking a tone when several
 * severities are in play. Thresholding is the server's job via
 * `FindingFilter.minSeverity`; this array is not a reimplementation of that
 * ranking and must not become one.
 */
export const SEVERITY_ORDER: readonly Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO']

/**
 * The tone for one finding.
 *
 * Two rules from the schema are enforced here rather than left to call sites.
 *
 * A passing finding never shows a severity. The server reports INFO on a pass
 * because `finding.Severity` is an int with no absent value, and rendering that
 * would put a severity grade on a clean result.
 *
 * An unrecognized status is UNKNOWN, never PASS. The status union is generated
 * from the schema, but the value arriving over the wire is a string and a
 * server that learns a fourth verdict must not have it read as a clean bill of
 * health here.
 */
export function findingTone(status: FindingStatus, severity: Severity): ToneStyle {
  if (status === 'PASS') {
    return { tone: 'pass', glyph: 'check', label: 'Pass' }
  }
  if (status === 'FAIL') {
    const tone = SEVERITY_TONE[severity] ?? 'info'
    return { tone, glyph: 'alert', label: SEVERITY_LABEL[severity] ?? 'Failed' }
  }
  return { tone: 'unknown', glyph: 'question', label: 'Could not tell' }
}

/** A check that never ran. Its own tone, because it is its own kind of news. */
export const UNTESTED_TONE: ToneStyle = {
  tone: 'untested',
  glyph: 'skip',
  label: 'Did not run',
}

export function severityTone(severity: Severity): Tone {
  return SEVERITY_TONE[severity] ?? 'info'
}

/**
 * Headings for the eight categories.
 *
 * The wording is the subject a reader is looking for, not the dotted prefix.
 * Somebody who wants to know whether their certificate is about to expire is
 * looking for "Transport security", not "TLS", so both are shown: the plain
 * name as the heading and the schema value as the anchor.
 *
 * An unrecognized category is shown under its own raw value rather than
 * dropped. New check families can introduce new categories and a finding that
 * vanished because the client did not recognize its heading would be a silent
 * false pass.
 */
const CATEGORY_TITLE: Readonly<Record<FindingCategory, string>> = {
  IP: 'Addressing',
  PORT: 'Listening services',
  DNS: 'DNS',
  TLS: 'Transport security',
  REDIRECT: 'Redirects',
  HTML: 'Page markup',
  STACK: 'Software disclosed',
  HEADERS: 'Response headers',
}

const CATEGORY_BLURB: Readonly<Record<FindingCategory, string>> = {
  IP: 'Where the name resolves, and whether those addresses answer.',
  PORT: 'Which ports answer on those addresses, and what each one is running.',
  DNS: 'Delegation, mail authentication, and the signing that anchors it.',
  TLS: 'Certificates, chains, protocol versions and the policies around them.',
  REDIRECT: 'Where the four entry points send a browser, and whether they agree.',
  HTML: 'What the homepage loads, and what its own markup gives away.',
  STACK: 'Products and versions the response names or implies.',
  HEADERS: 'The security headers the response sets, and the cookies it sets with them.',
}

/**
 * The order a request travels: an address, what is listening on it, the name
 * that produced it, the transport carrying it, where the entry points send a
 * browser, then three readings of the page it lands on.
 *
 * A report never needs this to order anything. `ScanSummary.byCategory` already
 * arrives in this sequence and the findings list is grouped by it, so both are
 * rendered in the order the server sent. This array is for the catalogue page,
 * where the only source is `Query.checks` sorted by id, which groups the
 * categories alphabetically instead. Ordering the headings there is a client
 * decision because the server was not asked the question.
 *
 * PORT is the one entry that is not in every report. The scanner sweeps ports
 * only when the caller asked for it and confirmed authorization, so most scans
 * produce none at all. Anything reading this array as a prediction of what a
 * running scan will contain has to allow for that rather than promise a row that
 * never fills. `SummaryPanel`'s reserved rows, which `ScanView` derives from
 * here, are the one place that reads it that way today.
 */
export const CATEGORY_ORDER: readonly FindingCategory[] = [
  'IP',
  'PORT',
  'DNS',
  'TLS',
  'REDIRECT',
  'HTML',
  'STACK',
  'HEADERS',
]

export function categoryTitle(category: string): string {
  return CATEGORY_TITLE[category as FindingCategory] ?? category
}

export function categoryBlurb(category: string): string {
  return CATEGORY_BLURB[category as FindingCategory] ?? ''
}

/** Stable, lower-cased anchor so a category section can be deep-linked. */
export function categoryAnchor(category: string): string {
  return `category-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}
